"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, GraduationCap, School, User, Loader2 } from "lucide-react";
import { FR } from "country-flag-icons/react/3x2";
import { IMaskInput } from "react-imask";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type UserType = "employer" | "employee" | "student" | "school";

const formSchema = z.object({
  userType: z.enum(["employer", "employee", "student", "school"]),
  organizationName: z.string().min(1, "Le nom de l'organisation est requis"),
  firstName: z.string().min(1, "Le prénom est requis"),
  lastName: z.string().min(1, "Le nom est requis"),
  email: z.string().email("L'adresse email n'est pas valide"),
  countryCode: z.string().default("+33"),
  phoneNumber: z
    .string()
    .regex(
      /^[0-9]\d{8}$/,
      "Le numéro de téléphone doit être au format 0X XX XX XX XX"
    ),
  message: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const AnimatedCheckmark = () => (
  <div className="rounded-full bg-green-100 px-2 w-full aspect-square flex items-center justify-center">
    <motion.svg
      xmlns="http://www.w3.org/2000/svg"
      width="100"
      height="100"
      viewBox="0 0 100 100"
    >
      <motion.path
        d="M20,50 L40,70 L80,30"
        fill="none"
        stroke="#22c55e"
        strokeWidth="8"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      />
    </motion.svg>
  </div>
);

const getOrganizationLabel = (userType: UserType | undefined) => {
  switch (userType) {
    case "employer":
      return "Nom de l'entreprise";
    case "employee":
      return "Nom de l'entreprise";
    case "student":
      return "Nom de l'école ou université";
    case "school":
      return "Nom de l'établissement";
    default:
      return "Nom de l'organisation";
  }
};

const countryCodes = [{ code: "+33", country: "France", flag: FR }];

const ContactDialog = ({
  children,
  trainingId,
}: {
  children: React.ReactNode;
  trainingId: string;
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userType: undefined,
      organizationName: "",
      firstName: "",
      lastName: "",
      email: "",
      countryCode: "+33",
      phoneNumber: "",
      message: "",
    },
  });

  useEffect(() => {
    if (step === 1) {
      form.setValue("userType", form.getValues("userType"));
    }
  }, [step, form]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      // // Format the phone number before submission
      // const formattedPhoneNumber = data.phoneNumber.replace(
      //   /(\d)(?=(\d{2})+(?!\d))/g,
      //   "$1 "
      // );

      const submissionData = {
        contact: data,
        trainingId,
      };

      // Make the HTTP call
      const response = await fetch("/api/contact/training", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit the form");
      }

      setStep(3); // Move to confirmation page
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Erreur",
        description:
          "Une erreur s'est produite lors de l'envoi du formulaire. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setOpen(false);
    setStep(1);
    form.reset();
  };

  const userTypeOptions = [
    { type: "employer", label: "Employeur", icon: Briefcase },
    { type: "employee", label: "Employé", icon: User },
    { type: "student", label: "Étudiant", icon: GraduationCap },
    { type: "school", label: "École", icon: School },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        {step !== 3 && (
          <DialogHeader>
            <DialogTitle>Contactez-nous</DialogTitle>
            <DialogDescription>
              {step === 1 && "Sélectionnez votre profil pour commencer"}
              {step === 2 && "Remplissez le formulaire pour nous contacter"}
            </DialogDescription>
          </DialogHeader>
        )}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 pt-4"
          >
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <FormField
                    control={form.control}
                    name="userType"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              setStep(2);
                            }}
                            className="grid grid-cols-2 gap-4"
                          >
                            {userTypeOptions.map((option) => (
                              <FormItem key={option.type}>
                                <FormControl>
                                  <RadioGroupItem
                                    value={option.type}
                                    id={option.type}
                                    className="peer sr-only"
                                  />
                                </FormControl>
                                <FormLabel
                                  htmlFor={option.type}
                                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                                  onClick={() => setStep(2)}
                                >
                                  <option.icon
                                    className={`mb-3 h-6 w-6 ${
                                      field.value === option.type
                                        ? "text-primary"
                                        : ""
                                    }`}
                                  />
                                  {option.label}
                                </FormLabel>
                              </FormItem>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </motion.div>
              )}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="organizationName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {getOrganizationLabel(form.watch("userType"))}{" "}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Prénom <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Nom <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Email <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field: { onChange, value, onBlur, name } }) => (
                      <FormItem>
                        <FormLabel>
                          Numéro de téléphone{" "}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="flex">
                            <FormField
                              control={form.control}
                              name="countryCode"
                              render={({ field }) => (
                                <Select
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                >
                                  <SelectTrigger className="w-[100px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {countryCodes.map((country) => (
                                      <SelectItem
                                        key={country.code}
                                        value={country.code}
                                      >
                                        <div className="flex items-center">
                                          <country.flag className="w-4 h-3 mr-2" />
                                          <span>{country.code}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                            <IMaskInput
                              name={name}
                              value={value}
                              onBlur={onBlur}
                              onAccept={(value, mask) => onChange(value)}
                              className="flex-1 ml-2 h-10 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border border-input bg-background rounded-r-md"
                              mask="0 00 00 00 00"
                              definitions={{
                                "0": /[0-9]/,
                              }}
                              unmask={true}
                              placeholder="X XX XX XX XX"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message (optionnel)</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={4} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </motion.div>
              )}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center justify-center space-y-6 py-6"
                >
                  <div className="w-24 h-24 mx-auto">
                    <AnimatedCheckmark />
                  </div>
                  <h3 className="text-2xl font-semibold text-center">
                    Demande envoyée avec succès !
                  </h3>
                  <p className="text-center text-muted-foreground">
                    Nous avons bien reçu votre demande et nous vous contacterons
                    dans les plus brefs délais.
                  </p>
                  <Button
                    type="button"
                    onClick={resetForm}
                    className="mt-4"
                    variant="outline"
                    size="sm"
                  >
                    Fermer
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex justify-between pt-4">
              {step === 2 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                >
                  Retour
                </Button>
              )}
              {step === 2 && (
                <Button type="submit" disabled={isLoading}>
                  {isLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Envoyer
                </Button>
              )}
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
